import {
  ReactElement,
  ChangeEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Stack,
  IconButton,
  Tooltip,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useFormikContext, FieldArray, ArrayHelpers } from 'formik';
import { get, isEmpty } from 'lodash';

import { DEFAULT_UUIID_EMPTY } from '~constants/commons';
import { useAppSelector } from '~hooks/index';
import { selectAuth } from '~store/slices/auth';
import { Nullable } from '~globals/types';
import { ItemCollectStatusTypes } from '~globals/types/enums';

import { useDialogFeedbackStopContext } from '../../../../DialogSetFeedbackStopContext';
import {
  FIELDS_NAME,
  ITEM_COLLECTS_FIELDS_NAME,
  getInitialValues,
} from '../../utils';

const NAME_FIELD = FIELDS_NAME.ITEM_COLLECTS;

const DialogSetFeedbackStopCompletedItemCollects = (): ReactElement => {
  const { user } = useAppSelector(selectAuth);

  const { data } = useDialogFeedbackStopContext();

  const { values, setFieldValue, setFieldTouched } =
    useFormikContext<ReturnType<typeof getInitialValues>>();

  const toPathField = useCallback(
    (index: number, keyField: string) => `${NAME_FIELD}[${index}][${keyField}]`,
    [],
  );

  const itemCollectsList = useMemo(() => values[NAME_FIELD], [values]);

  const [newItemCollectDesc, setNewItemCollectDesc] = useState('');

  const handleChangeNewItemCollectDesc = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target ?? event.currentTarget;

      setNewItemCollectDesc(value);
    },
    [],
  );

  const handleChangeIsCollected = useCallback(
    (event: ChangeEvent<HTMLInputElement>, index: number) => {
      const { checked } = event.target ?? event.currentTarget;

      const value = checked
        ? ItemCollectStatusTypes.Collected
        : ItemCollectStatusTypes.NotCollected;

      setFieldValue(
        toPathField(
          index,
          ITEM_COLLECTS_FIELDS_NAME.ITEM_COLLECT_STATUS_TYPE_ID,
        ),
        value,
      );
    },
    [setFieldValue, toPathField],
  );

  const handleBlurIsCollected = useCallback(
    (index: number) => {
      setFieldTouched(
        toPathField(
          index,
          ITEM_COLLECTS_FIELDS_NAME.ITEM_COLLECT_STATUS_TYPE_ID,
        ),
        true,
      );
    },
    [setFieldTouched, toPathField],
  );

  const handleAddItemCollectInList = useCallback(
    (insertHelper: ArrayHelpers['insert']) => {
      if (newItemCollectDesc && data?.currentItemId && user) {
        const referenceId = newItemCollectDesc.startsWith('RO')
          ? null
          : newItemCollectDesc;

        const trackingNumber = !newItemCollectDesc.startsWith('RO')
          ? null
          : newItemCollectDesc;

        const newArticle = {
          [ITEM_COLLECTS_FIELDS_NAME.ITEM_COLLECT_ID]: DEFAULT_UUIID_EMPTY,
          [ITEM_COLLECTS_FIELDS_NAME.ITEM_ID]: data.currentItemId,
          [ITEM_COLLECTS_FIELDS_NAME.COMPANY_ID]: user.companyId,
          [ITEM_COLLECTS_FIELDS_NAME.USER_ID]: user.userId,
          [ITEM_COLLECTS_FIELDS_NAME.DESCRIPTION]: null,
          [ITEM_COLLECTS_FIELDS_NAME.NOTES]: null,
          [ITEM_COLLECTS_FIELDS_NAME.PARTENR_ITEM_ID]: null,
          [ITEM_COLLECTS_FIELDS_NAME.REFERENCE_ID]: referenceId,
          [ITEM_COLLECTS_FIELDS_NAME.TRACKING_NUMBER]: trackingNumber,
          [ITEM_COLLECTS_FIELDS_NAME.ITEM_COLLECT_STATUS_TYPE_ID]:
            ItemCollectStatusTypes.Collected,
          [ITEM_COLLECTS_FIELDS_NAME.ITEM_COLLECT_STATUS_TYPE]: null,
          [ITEM_COLLECTS_FIELDS_NAME.CREATION_DATE_TIME]: null,
          [ITEM_COLLECTS_FIELDS_NAME.DELETED]: null,
          [ITEM_COLLECTS_FIELDS_NAME.IS_OWNER]: true,
        };

        insertHelper(0, newArticle);

        setNewItemCollectDesc('');
      }
    },
    [newItemCollectDesc, data, user],
  );

  const getValueForIndex = useCallback(
    <R,>(index: number, fielName: string) =>
      get(values, toPathField(index, fielName)) as R,
    [toPathField, values],
  );

  const getDescriptionValueForIndex = useCallback(
    (index: number) => {
      const referenceId = getValueForIndex<Nullable<string>>(
        index,
        ITEM_COLLECTS_FIELDS_NAME.REFERENCE_ID,
      );

      const trackingNumber = getValueForIndex<Nullable<string>>(
        index,
        ITEM_COLLECTS_FIELDS_NAME.TRACKING_NUMBER,
      );

      return isEmpty(referenceId) ? trackingNumber : referenceId;
    },
    [getValueForIndex],
  );

  const getCollectValueForIndex = useCallback(
    (index: number) => {
      const itemCollectStatusTypeId = getValueForIndex<ItemCollectStatusTypes>(
        index,
        ITEM_COLLECTS_FIELDS_NAME.ITEM_COLLECT_STATUS_TYPE_ID,
      );

      return itemCollectStatusTypeId === ItemCollectStatusTypes.Collected;
    },
    [getValueForIndex],
  );

  return (
    <FieldArray name={NAME_FIELD}>
      {({ insert, remove }) => (
        <Stack spacing={3} divider={<Divider />}>
          <Stack spacing={1} direction="row" alignItems="center">
            <TextField
              label="Nuevo pedido"
              InputLabelProps={{ shrink: true }}
              size="small"
              value={newItemCollectDesc}
              onChange={handleChangeNewItemCollectDesc}
              autoComplete="off"
              fullWidth
            />

            <Tooltip title="Agregar pedido" arrow placement="right">
              <span>
                <IconButton
                  aria-label="Add item collect"
                  onClick={() => handleAddItemCollectInList(insert)}
                  color="primary"
                  size="small"
                  disabled={!newItemCollectDesc}
                >
                  <AddIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {!itemCollectsList.length && (
            <Typography variant="h6" fontWeight="bold" textAlign="center">
              No hay pedidos cargados
            </Typography>
          )}

          {!!itemCollectsList.length && (
            <Stack spacing={2} divider={<Divider />}>
              {itemCollectsList.map((_itemCollect, itemCollectIndex) => (
                <Stack
                  key={toPathField(itemCollectIndex, 'root')}
                  spacing={1}
                  direction="row"
                  alignItems="center"
                >
                  <Stack spacing={1} width="100%">
                    <TextField
                      label="Descripción"
                      value={getDescriptionValueForIndex(itemCollectIndex)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      disabled
                      autoComplete="off"
                      fullWidth
                    />
                  </Stack>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={getCollectValueForIndex(itemCollectIndex)}
                        onChange={(event) =>
                          handleChangeIsCollected(event, itemCollectIndex)
                        }
                        onBlur={() => handleBlurIsCollected(itemCollectIndex)}
                      />
                    }
                    label="Colectado"
                  />

                  {getValueForIndex<boolean>(
                    itemCollectIndex,
                    ITEM_COLLECTS_FIELDS_NAME.IS_OWNER,
                  ) && (
                    <Tooltip title="Eliminar pedido" arrow placement="right">
                      <span>
                        <IconButton
                          aria-label="Delete item collect"
                          onClick={() => remove(itemCollectIndex)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}
    </FieldArray>
  );
};

export default DialogSetFeedbackStopCompletedItemCollects;
